var __rest = this && this.__rest || function (s, e) {
  var t = {};
  for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0) t[p] = s[p];
  if (s != null && typeof Object.getOwnPropertySymbols === "function") for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
    if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i])) t[p[i]] = s[p[i]];
  }
  return t;
};
import React, { isValidElement, forwardRef, useEffect, useState } from 'react';
typeof window !== 'undefined' && !window.__DS_WEB_VERSION__ && (window.__DS_WEB_VERSION__ = '2.10.0');
import { Flex } from '../Flex';
import { Text } from '../Text';
import { isEmpty } from '../../helpers';
import { dropdownDefaultProps } from './props';
import * as S from './styles';
const ACCESSIBILITY_LABEL_ERROR = '[Dropdown - Accessibility Error] O elemento select deve ter um label associado. Informe pelo menos um dos atributos "label" | "aria-label" | "aria-labelledby" com valor válido.';
const ACCESSIBILITY_LOADING_ERROR = '[Dropdown - Accessibility Error] Quando o state for "loading" é obrigatório informar um "stateMessage" para indicar para os leitores de tela a mensagem de carregamento.';
const LABEL_WITHOUT_ID_ERROR = '[Dropdown - Error] Para utilização da label no componente é obrigatório a utilização de um id válido.';
const validateAccessibilityProps = _ref => {
  let {
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledby,
    label,
    state,
    stateMessage
  } = _ref;
  if (!isValidElement(label) && typeof label === 'string' && isEmpty(label) && isEmpty(ariaLabel) && isEmpty(ariaLabelledby)) {
    throw new Error(ACCESSIBILITY_LABEL_ERROR);
  }
  if (state === 'loading' && isEmpty(stateMessage)) throw new Error(ACCESSIBILITY_LOADING_ERROR);
};
const validateLabelAndIdProps = _ref2 => {
  let {
    id,
    label
  } = _ref2;
  if (!isValidElement(label) && typeof label === 'string' && isEmpty(id)) throw new Error(LABEL_WITHOUT_ID_ERROR);
};
const DropdownItems = _ref3 => {
  let {
    items,
    placeholder,
    required
  } = _ref3;
  return React.createElement(React.Fragment, null, placeholder && React.createElement("option", {
    key: "-1",
    value: "",
    label: placeholder,
    disabled: required
  }, placeholder), items === null || items === void 0 ? void 0 : items.map((item, index) => React.createElement("option", Object.assign({
    key: index
  }, item), item.label)));
};
const SuperiorContent = _ref4 => {
  let {
    id,
    label,
    size
  } = _ref4;
  if (size === 'medium') return React.createElement(Text.BodyMedium, {
    tag: "label",
    htmlFor: id,
    marginBottom: "--spacing-1",
    weight: "semibold"
  }, label);
  if (size === 'small') return React.createElement(Text.BodySmall, {
    tag: "label",
    htmlFor: id,
    marginBottom: "--spacing-0-5",
    weight: "semibold"
  }, label);
  return null;
};
const InferiorContent = _ref5 => {
  let {
    caption,
    errorId,
    state,
    stateMessage
  } = _ref5;
  if (state === 'error' && stateMessage) return React.createElement(Text.Caption, {
    color: "--color-feedback-error-100",
    role: "alert",
    id: errorId,
    marginTop: "--spacing-1"
  }, stateMessage);
  if (caption) return React.createElement(Text.Caption, {
    color: "--color-neutral-120",
    marginTop: "--spacing-1"
  }, caption);
  return null;
};
/**
 * Componente Dropdown do Design System OLX
 */
export const Dropdown = forwardRef((props, ref) => {
  var _a;
  const [selected, setSelected] = useState(props.defaultValue || props.value);
  const [errorId, setErrorId] = useState('');
  const {
      size
    } = props,
    propsWithoutSize = __rest(props, ["size"]);
  const variant = size || dropdownDefaultProps.size;
  const chevronIconSize = size === 'medium' ? 24 : 16;
  useEffect(() => {
    setErrorId("error-".concat(Math.floor(Math.random() * 101)));
  }, []);
  useEffect(() => {
    if (props.defaultValue || props.value) {
      setSelected(props.defaultValue || props.value);
    } else {
      setSelected('');
    }
  }, [props.defaultValue, props.value]);
  validateAccessibilityProps(props);
  validateLabelAndIdProps(props);
  return React.createElement(Flex, {
    "data-ds-component": "DS-Dropdown",
    flexDirection: "column"
  }, props.label && React.createElement(SuperiorContent, Object.assign({}, props)), React.createElement(S.SelectWrapper, {
    variant: variant
  }, props.iconLeft && React.createElement(S.IconWrapper, {
    selected: !!selected,
    variant: variant
  }, props.iconLeft), React.createElement(S.SelectNativeInput, Object.assign({}, propsWithoutSize, {
    "aria-busy": props.state === 'loading',
    "aria-errormessage": props.state === 'error' ? errorId : undefined,
    "aria-invalid": props.state === 'error',
    defaultValue: (_a = props.defaultValue) !== null && _a !== void 0 ? _a : undefined,
    error: props.state === 'error',
    onChange: event => {
      setSelected(event.target.value);
      if (props.onChange) props.onChange(event);
    },
    ref: ref,
    selected: !!selected,
    variant: variant
  }), React.createElement(DropdownItems, Object.assign({}, props))), props.state === 'loading' ?
  // props.stateMessage é validado na função validateAccessibilityProps
  React.createElement(S.LoadingIcon, {
    "aria-label": props.stateMessage,
    size: "extra-small",
    variant: variant
  }) : React.createElement(S.ChevronIcon, {
    disabled: props.disabled,
    size: chevronIconSize,
    variant: variant
  })), React.createElement(InferiorContent, Object.assign({}, props, {
    errorId: errorId
  })));
});
Dropdown.defaultProps = dropdownDefaultProps;
Dropdown.displayName = 'DS-Dropdown';